// M3 forwards, futures and puts (M3_SPEC §9.5). Every price comes from today's spot and the forward-curve slope of
// the regime in force today (rules.forwardPrice / curveSlope): the curve never reads the future price path, so a hedge
// carries no foresight. Hedges are capped at the next 12 months of the company's production (hedging, not betting).
//   forward (from 16 Oct 1973): sell N bbl/month for 1-12 months at F(m) × (1 − 3%); settled monthly in cash against
//     spot; counterparty default 1%/yr (a contract in your favour is lost).
//   future (NYMEX, from 30 Mar 1983): 1,000-bbl contracts, listed months futuresMonths(y); initial margin 10% of
//     notional into a margin account, daily mark-to-market vs F(monthsLeft); below 7.5% maintenance → margin call
//     (topped up from cash, else liquidated); $25 commission per round trip; expires at spot.
//   put (NYMEX options, from 14 Nov 1986): premium by the Bachelier formula on F with 35%/yr volatility.
import { HEDGING, forwardPrice, futuresMonths, curveSlope } from '../../data/m3/rules.js';
import { dailyFromAnnual } from '../../data/m2/rules.js';
import { chance } from '../../core/rng.js';
import { dateToDay } from '../../core/time.js';
import { spotPrice } from '../../economy/market.js';
import { eraDollars, yf } from '../defs.js';
import { notify, book, fmt$, clamp } from '../util.js';
import { bumpStat } from './econ.js';
import { m4FuturesSettle, m4HedgeSpot } from '../m4/marketNegative.js'; // M4-B: contracts expiring 20 Apr 2020 settle at −$37.63

export const OPTION_VOL = 0.35;          // annual volatility used to price puts (est.)
export const PUT_SPREAD = 0.05;          // bid/ask haircut when selling a put back
export const FORWARD_UNWIND_PENALTY = 0.02;
export const DAYS_PER_MONTH = 30.4;
export const HEDGE_CAP_MONTHS = 12;
// Day-exact openings (the fractional years in rules.HEDGING round to the month)
export const FORWARDS_DAY = dateToDay(1973, 10, 16);
export const NYMEX_DAY = dateToDay(1983, 3, 30);
export const OPTIONS_DAY = dateToDay(1986, 11, 14);
export const BRENT_FUTURES_DAY = dateToDay(1988, 6, 23);

export function hedgeState(state) {
  state.hedging ??= { positions: [], realized: 0, nextId: 1 };
  state.hedging.positions ??= [];
  return state.hedging;
}

const spot = (state) => Math.max(0.01, m4HedgeSpot(state) ?? spotPrice(state)); // M4-B: on 20 Apr 2020 only the expiring May contract is negative
const commission = (state) => eraDollars(state, HEDGING.commissionPerRoundTrip, 1983);
const monthsLeft = (state, p) => Math.max(0, (p.expiryDay - state.day) / DAYS_PER_MONTH);

// Company production (bbl/d) the hedge cap is based on.
function productionBbld(state) {
  let q = 0;
  for (const w of Object.values(state.wells)) if (w.status === 'PRODUCING' && w.prod?.rate > 0 && !w.ownerOperatorId) q += w.prod.rate;
  return q;
}
export function hedgeCapacityBbl(state) { return productionBbld(state) * DAYS_PER_MONTH * HEDGE_CAP_MONTHS; }
export function hedgedBbl(state) {
  let b = 0;
  for (const p of state.hedging?.positions ?? []) {
    if (p.kind === 'forward') b += p.months.filter((m) => !m.settled).length * p.bblPerMonth;
    else if (p.side !== 'long') b += p.bbl;
  }
  return b;
}

const N = (x) => 0.5 * (1 + erf(x / Math.SQRT2));
const phi = (x) => Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI);
function erf(x) { // Abramowitz-Stegun 7.1.26
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return x >= 0 ? y : -y;
}
// Bachelier put on F, strike K, T years.
export function putValue(F, K, T) {
  if (!(T > 0)) return Math.max(0, K - F);
  const s = OPTION_VOL * F * Math.sqrt(T);
  const d = (K - F) / s;
  return (K - F) * N(d) + s * phi(d);
}

export function quoteHedge(state, kind, { months = 6, strike = null } = {}) {
  const y = yf(state);
  const S = spot(state);
  const m = clamp(Math.round(months), 1, kind === 'forward' ? HEDGING.forwardMaxMonths : Math.max(1, futuresMonths(Math.max(y, state.day >= NYMEX_DAY ? HEDGING.nymexFrom : 0))));
  const F = forwardPrice(S, m, y);
  const out = { kind, months: m, spot: S, forward: F, slope: curveSlope(y), commission: commission(state), available: true, reason: null };
  if (kind === 'forward') { out.price = F * (1 - HEDGING.forwardDiscount); if (state.day < FORWARDS_DAY) { out.available = false; out.reason = 'Term contracts with refiners start in the oil crisis (Oct 1973).'; } }
  if (kind === 'future') { out.price = F; out.margin = F * HEDGING.contractBbl * HEDGING.initialMarginFrac; if (state.day < NYMEX_DAY) { out.available = false; out.reason = 'NYMEX crude futures start trading on 30 March 1983.'; } }
  if (kind === 'put') { const K = strike ?? Math.round(S * 0.9 * 100) / 100; out.strike = K; out.premium = putValue(F, K, m / 12); if (state.day < OPTIONS_DAY) { out.available = false; out.reason = 'NYMEX crude options start on 14 November 1986.'; } }
  return out;
}

function capCheck(state, bbl) {
  const cap = hedgeCapacityBbl(state);
  if (hedgedBbl(state) + bbl > cap + 1e-6) return `You may hedge at most a year of production (${Math.round(cap).toLocaleString('en-US')} bbl; ${Math.round(hedgedBbl(state)).toLocaleString('en-US')} already hedged).`;
  return null;
}

export function sellForwardImpl(state, { bblPerMonth, months = 6 } = {}) {
  const q = quoteHedge(state, 'forward', { months });
  if (!q.available) return { ok: false, msg: q.reason };
  const b = Math.round(Number(bblPerMonth) || 0);
  if (!(b > 0)) return { ok: false, msg: 'Enter barrels per month.' };
  const cap = capCheck(state, b * q.months);
  if (cap) return { ok: false, msg: cap };
  const H = hedgeState(state);
  const S = spot(state), y = yf(state);
  const legs = [];
  for (let i = 1; i <= q.months; i++) legs.push({ due: state.day + Math.round(i * DAYS_PER_MONTH), price: Math.round(forwardPrice(S, i, y) * (1 - HEDGING.forwardDiscount) * 100) / 100, settled: false });
  const p = { id: `h${H.nextId++}`, kind: 'forward', side: 'short', bblPerMonth: b, bbl: b * q.months, openDay: state.day, expiryDay: legs[legs.length - 1].due, months: legs, pnl: 0 };
  H.positions.push(p);
  return { ok: true, msg: `Sold ${b.toLocaleString('en-US')} bbl a month forward for ${q.months} months at ${fmt$(legs[0].price)}–${fmt$(legs[legs.length - 1].price)}.` };
}

export function openFutureImpl(state, { contracts = 1, months = 3, side = 'short' } = {}) {
  const q = quoteHedge(state, 'future', { months });
  if (!q.available) return { ok: false, msg: q.reason };
  const n = Math.round(Number(contracts) || 0);
  if (!(n > 0)) return { ok: false, msg: 'Enter a number of contracts.' };
  if (!['short', 'long'].includes(side)) return { ok: false, msg: 'Side is short or long.' };
  const bbl = n * HEDGING.contractBbl;
  if (side === 'short') { const cap = capCheck(state, bbl); if (cap) return { ok: false, msg: cap }; }
  else if (bbl > hedgedBbl(state) + 1e-6) return { ok: false, msg: 'Long futures only offset an existing short hedge.' };
  const margin = q.margin * n;
  const fee = q.commission * n;
  if (state.company.cash < margin + fee) return { ok: false, msg: `Initial margin ${fmt$(margin)} + commission ${fmt$(fee)} needed.` };
  const H = hedgeState(state);
  state.company.cash -= margin + fee;
  book(state, 'hedging', -fee);
  H.realized -= fee;
  const p = { id: `h${H.nextId++}`, kind: 'future', side, contracts: n, bbl, price: Math.round(q.price * 100) / 100, mark: q.price, openDay: state.day, expiryDay: state.day + Math.round(q.months * DAYS_PER_MONTH), margin, deposited: margin, pnl: 0 };
  H.positions.push(p);
  bumpStat(state, 'futuresContracts', n);
  return { ok: true, msg: `${side === 'short' ? 'Sold' : 'Bought'} ${n} NYMEX contract${n > 1 ? 's' : ''} (${bbl.toLocaleString('en-US')} bbl) for ${q.months} months at ${fmt$(q.price)}. Margin ${fmt$(margin)}.` };
}

export function buyPutImpl(state, { contracts = 1, months = 3, strike = null } = {}) {
  const q = quoteHedge(state, 'put', { months, strike });
  if (!q.available) return { ok: false, msg: q.reason };
  const n = Math.round(Number(contracts) || 0);
  if (!(n > 0)) return { ok: false, msg: 'Enter a number of contracts.' };
  const bbl = n * HEDGING.contractBbl;
  const cap = capCheck(state, bbl);
  if (cap) return { ok: false, msg: cap };
  const cost = q.premium * bbl + q.commission * n;
  if (state.company.cash < cost) return { ok: false, msg: `The premium is ${fmt$(cost)}.` };
  const H = hedgeState(state);
  state.company.cash -= cost;
  book(state, 'hedging', -cost);
  H.realized -= cost;
  H.positions.push({ id: `h${H.nextId++}`, kind: 'put', side: 'short', contracts: n, bbl, strike: q.strike, premium: q.premium, openDay: state.day, expiryDay: state.day + Math.round(q.months * DAYS_PER_MONTH), pnl: -cost });
  bumpStat(state, 'futuresContracts', n);
  return { ok: true, msg: `Bought ${n} put${n > 1 ? 's' : ''} at ${fmt$(q.strike)} for ${fmt$(q.premium)}/bbl (${fmt$(cost)}).` };
}

function closeFuture(state, p, reason = 'closed') {
  const H = hedgeState(state);
  state.company.cash += p.margin;
  const pnl = p.margin - p.deposited;
  book(state, 'hedging', pnl);
  H.realized += pnl;
  p.pnl = pnl;
  H.positions = H.positions.filter((x) => x !== p);
  return { pnl, reason };
}

export function closeHedgeImpl(state, id) {
  const H = hedgeState(state);
  const p = H.positions.find((x) => x.id === id);
  if (!p) return { ok: false, msg: 'No such position.' };
  const y = yf(state);
  if (p.kind === 'future') {
    markFuture(state, p);
    const r = closeFuture(state, p);
    return { ok: true, msg: `Closed ${p.contracts} contract${p.contracts > 1 ? 's' : ''}: ${r.pnl >= 0 ? 'gain' : 'loss'} ${fmt$(Math.abs(r.pnl))}.` };
  }
  if (p.kind === 'put') {
    const v = putValue(forwardPrice(spot(state), monthsLeft(state, p), y), p.strike, monthsLeft(state, p) / 12) * (1 - PUT_SPREAD) * p.bbl;
    state.company.cash += v; book(state, 'hedging', v); H.realized += v;
    H.positions = H.positions.filter((x) => x !== p);
    return { ok: true, msg: `Sold the puts back for ${fmt$(v)}.` };
  }
  // forward: unwind remaining legs at today's curve, less a penalty
  let v = 0;
  const S = spot(state);
  for (const leg of p.months) if (!leg.settled) { const m = Math.max(0, (leg.due - state.day) / DAYS_PER_MONTH); v += (leg.price - forwardPrice(S, m, y)) * p.bblPerMonth; }
  v -= FORWARD_UNWIND_PENALTY * S * p.months.filter((l) => !l.settled).length * p.bblPerMonth;
  state.company.cash += v; book(state, 'hedging', v); H.realized += v;
  H.positions = H.positions.filter((x) => x !== p);
  return { ok: true, msg: `Forward contract unwound: ${v >= 0 ? 'you receive' : 'you pay'} ${fmt$(Math.abs(v))}.` };
}

function markFuture(state, p) {
  const F = p.expiryDay <= state.day ? (m4FuturesSettle(state) ?? spot(state)) : forwardPrice(spot(state), monthsLeft(state, p), yf(state)); // M4-B: negative settle
  const d = (F - p.mark) * p.bbl * (p.side === 'short' ? -1 : 1);
  p.margin += d;
  p.mark = F;
  return F;
}

// Daily (from 16 Oct 1973, only when positions exist).
export function tickHedging(state) {
  const H = state.hedging;
  if (!H?.positions?.length || state.day < FORWARDS_DAY) return;
  const S = spot(state);
  for (const p of [...H.positions]) {
    if (p.kind === 'forward') {
      for (const leg of p.months) {
        if (leg.settled || state.day < leg.due) continue;
        leg.settled = true;
        const v = (leg.price - S) * p.bblPerMonth;
        state.company.cash += v; book(state, 'hedging', v); H.realized += v; p.pnl += v;
      }
      if (p.months.every((l) => l.settled)) { H.positions = H.positions.filter((x) => x !== p); continue; }
      // counterparty default: a refiner walks away from a contract that has turned against it
      const mtm = p.months.filter((l) => !l.settled).reduce((a, l) => a + (l.price - S) * p.bblPerMonth, 0);
      if (mtm > 0 && chance(state, dailyFromAnnual(HEDGING.forwardCounterpartyDefaultPerYear))) {
        H.positions = H.positions.filter((x) => x !== p);
        notify(state, 'news', { kind: 'finance', level: 'warn', text: `Your forward buyer has defaulted: ${fmt$(mtm)} of hedge value is gone.` });
      }
    } else if (p.kind === 'future') {
      const F = markFuture(state, p);
      if (state.day >= p.expiryDay) { const r = closeFuture(state, p, 'expired'); notify(state, 'news', { kind: 'finance', text: `${p.contracts} NYMEX contract${p.contracts > 1 ? 's' : ''} expired at ${fmt$(S)}: ${r.pnl >= 0 ? 'gain' : 'loss'} ${fmt$(Math.abs(r.pnl))}.` }); continue; }
      const maint = F * p.bbl * HEDGING.maintenanceMarginFrac;
      if (p.margin < maint) {
        const need = F * p.bbl * HEDGING.initialMarginFrac - p.margin;
        if (state.company.cash >= need) {
          state.company.cash -= need; p.margin += need; p.deposited += need;
          notify(state, 'marginCall', { positionId: p.id, amount: need, liquidated: false, pause: false, level: 'warn', text: `Margin call: ${fmt$(need)} wired to the clearing house to keep ${p.contracts} contract${p.contracts > 1 ? 's' : ''} open.` });
        } else {
          const r = closeFuture(state, p, 'liquidated');
          notify(state, 'marginCall', { positionId: p.id, amount: need, liquidated: true, pause: true, level: 'major', text: `Margin call of ${fmt$(need)} unmet: your broker liquidated ${p.contracts} contract${p.contracts > 1 ? 's' : ''} (${fmt$(r.pnl)}).` });
        }
      }
    } else if (p.kind === 'put' && state.day >= p.expiryDay) {
      const v = Math.max(0, p.strike - S) * p.bbl;
      if (v > 0) { state.company.cash += v; book(state, 'hedging', v); H.realized += v; }
      p.pnl += v;
      H.positions = H.positions.filter((x) => x !== p);
      notify(state, 'news', { kind: 'finance', text: `Puts at ${fmt$(p.strike)} expired ${v > 0 ? `in the money: ${fmt$(v)}` : 'worthless'}.` });
    }
  }
}

// Value of open hedges for net worth: futures margin accounts (plus their open P&L) and put values.
export function hedgeAccountValue(state) {
  let v = 0;
  for (const p of state.hedging?.positions ?? []) {
    if (p.kind === 'future') v += p.margin;
    else if (p.kind === 'put') v += Math.max(0, p.strike - spot(state)) * p.bbl;
  }
  return v;
}

export function hedgeView(state) {
  const y = yf(state);
  const S = spot(state);
  const positions = (state.hedging?.positions ?? []).map((p) => {
    const ml = monthsLeft(state, p);
    let mtm = 0;
    if (p.kind === 'future') mtm = p.margin - p.deposited;
    else if (p.kind === 'put') mtm = putValue(forwardPrice(S, ml, y), p.strike, ml / 12) * p.bbl - p.premium * p.bbl;
    else mtm = p.months.filter((l) => !l.settled).reduce((a, l) => a + (l.price - forwardPrice(S, Math.max(0, (l.due - state.day) / DAYS_PER_MONTH), y)) * p.bblPerMonth, 0);
    return { ...p, monthsLeft: Math.round(ml * 10) / 10, mtm: Math.round(mtm), months: p.months ? p.months.map((l) => ({ ...l })) : undefined };
  });
  return {
    year: y, spot: S, slope: curveSlope(y), curve: [1, 3, 6, 12, 24, 36].filter((m) => m <= Math.max(12, futuresMonths(y))).map((m) => ({ months: m, price: forwardPrice(S, m, y) })),
    forwards: quoteHedge(state, 'forward'), futures: { ...quoteHedge(state, 'future'), listedMonths: futuresMonths(y) }, puts: quoteHedge(state, 'put'),
    brentFutures: state.day >= BRENT_FUTURES_DAY, positions, realized: state.hedging?.realized ?? 0,
    capacityBbl: hedgeCapacityBbl(state), hedgedBbl: hedgedBbl(state), accountValue: hedgeAccountValue(state),
    basisRisk: HEDGING.basisRisk,
  };
}
